import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SupportStatus, supportDate } from "@/components/SupportStatus";
import { SupportMessage } from "@/components/SupportMessage";
import { SupportFilePicker } from "@/components/SupportFilePicker";
import { SupportFirstReplyDialog } from "@/components/SupportFirstReplyDialog";
import { SupportResponseTimeNote } from "@/components/SupportResponseTimeNote";
import { SupportDeleteDialog } from "@/components/SupportDeleteDialog";
import { useSupportNotifications } from "@/lib/SupportNotificationsContext";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export const SupportTicketConversation = ({ ticketId, admin, basePath, liveEvent }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const notices = useSupportNotifications();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [status, setStatus] = useState("");
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmFirstReply, setConfirmFirstReply] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [impactLoading, setImpactLoading] = useState(false);
  const [createdFor, setCreatedFor] = useState(() => location.state?.ticketCreated ? ticketId : null);
  const [error, setError] = useState("");
  const currentTicket = useRef(null);
  const selectedStatus = useRef("");
  const sending = useRef(false);
  const deleteTriggerRef = useRef(null);
  const deleteReturnFocusRef = useRef(null);

  useEffect(() => {
    if (!admin && location.state?.ticketCreated) {
      setCreatedFor(ticketId);
      navigate(location.pathname + location.search, { replace: true, state: null });
    } else if (createdFor && createdFor !== ticketId) setCreatedFor(null);
  }, [admin, ticketId, location.state?.ticketCreated, location.pathname, location.search, navigate, createdFor]);

  const applyTicket = useCallback((data, resetSelection = false) => {
    if (currentTicket.current && data.updated_at < currentTicket.current.updated_at) return;
    if (resetSelection || !currentTicket.current || selectedStatus.current === currentTicket.current.status) {
      selectedStatus.current = data.status;
      setStatus(data.status);
    }
    currentTicket.current = data;
    setTicket(data);
  }, []);

  useEffect(() => {
    let active = true;
    currentTicket.current = null; selectedStatus.current = "";
    setTicket(null); setReply(""); setFiles([]); setConfirmFirstReply(false);
    setDeleteImpact(null); setLoading(true); setError("");
    api.get(`/support/tickets/${ticketId}`).then(({ data }) => {
      if (active) {
        applyTicket(data, true); setLoading(false);
        notices?.markTicketRead(ticketId).catch(() => {});
      }
    }).catch(() => { if (active) { setError("Ticket non trovato o non accessibile."); setLoading(false); } });
    return () => { active = false; };
    // The selected draft and active ticket are reset only when ticketId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, applyTicket]);

  useEffect(() => {
    if (!ticket || !liveEvent?.id || liveEvent.ticket_id !== ticketId) return undefined;
    if (liveEvent.deleted) {
      currentTicket.current = null;
      setTicket(null);
      setError("Questo ticket è stato eliminato dall'Assistenza.");
      setLoading(false);
      return undefined;
    }
    let active = true;
    api.get(`/support/tickets/${ticketId}`).then(({ data }) => {
      if (!active) return;
      applyTicket(data);
      notices?.markTicketRead(ticketId).catch(() => {});
    }).catch(() => {}); // A subsequent event or focus will fetch the latest snapshot.
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, ticketId, liveEvent?.id, applyTicket]);

  useEffect(() => {
    if (confirmFirstReply && ticket?.status !== "open") {
      setConfirmFirstReply(false);
      setError("Lo stato del ticket è cambiato. Controllalo prima di inviare la risposta.");
    }
  }, [confirmFirstReply, ticket?.status]);

  const sendReply = async (nextStatus = status) => {
    if (sending.current || !ticket) return;
    sending.current = true;
    setBusy(true); setError("");
    try {
      const form = new FormData();
      files.forEach(file => form.append("files", file));
      if (admin) {
        form.append("status", nextStatus === ticket.status ? "" : nextStatus);
        form.append("reply", reply.trim());
      } else form.append("body", reply.trim());
      const { data } = admin
        ? await api.patch(`/support/admin/tickets/${ticketId}/with-attachments`, form)
        : await api.post(`/support/tickets/${ticketId}/messages/with-attachments`, form);
      applyTicket(data, true); setReply(""); setFiles([]); setConfirmFirstReply(false);
    } catch (e) {
      setConfirmFirstReply(false);
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Impossibile inviare la risposta.");
      if (e?.response?.status === 409) {
        api.get(`/support/tickets/${ticketId}`).then(({ data }) => applyTicket(data)).catch(() => {});
      }
    } finally { sending.current = false; setBusy(false); }
  };

  const submit = event => {
    event.preventDefault();
    if (busy || !ticket) return;
    const firstResponse = admin && ticket.status === "open" && status === "open" &&
      !ticket.messages.some(message => message.kind === "admin") && (reply.trim() || files.length > 0);
    if (firstResponse) { setConfirmFirstReply(true); return; }
    sendReply();
  };

  const openDelete = async () => {
    if (impactLoading) return;
    deleteReturnFocusRef.current = deleteTriggerRef.current;
    setImpactLoading(true); setDeleteError("");
    try {
      const { data } = await api.get(`/support/admin/tickets/${ticketId}/deletion-impact`);
      setDeleteImpact(data);
    } catch (e) { setError(e?.response?.data?.detail || "Impossibile verificare il ticket. Riprova."); }
    finally { setImpactLoading(false); }
  };

  const confirmDelete = async () => {
    if (deleteBusy) return;
    setDeleteBusy(true); setDeleteError("");
    try {
      await api.delete(`/support/admin/tickets/${ticketId}`);
      setDeleteImpact(null);
      notices?.refresh();
      navigate(basePath);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || "Eliminazione non completata. Riprova.");
      // Refresh the impact to reflect any deletion already performed before an error.
      api.get(`/support/admin/tickets/${ticketId}/deletion-impact`).then(({ data }) => setDeleteImpact(data)).catch(() => {});
    } finally { setDeleteBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="support-conversation">
      <button type="button" onClick={() => navigate(basePath)} className="inline-flex items-center gap-2 text-sm text-[#707070] hover:text-[#1A1A1A] mb-8"
              data-testid="support-conversation-back"><ArrowLeft className="w-4 h-4"/>Tutti i ticket</button>
      {loading && <p className="py-10 text-sm" data-testid="support-conversation-loading">Caricamento...</p>}
      {error && <p className="text-sm text-[#8C3A3A] mb-5" role="alert" data-testid="support-conversation-error">{error}</p>}
      {ticket && <>
        <div className="flex flex-wrap gap-4 items-start justify-between border-b border-[#E4E3DE] pb-7">
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-4xl sm:text-5xl break-words text-[#1A1A1A]" data-testid="support-conversation-subject">{ticket.subject}</h1>
            {admin && <div className="border-l-4 border-[#658870] pl-3 mt-4" data-testid="support-conversation-customer">
              <span className="block text-xs uppercase font-semibold text-[#50745D]">Richiesta da</span>
              <strong className="block text-base break-words" data-testid="support-conversation-customer-name">
                {ticket.author_name || ticket.author_email}
              </strong>
              <span className="block text-sm text-[#535B52] break-words" data-testid="support-conversation-company">
                {ticket.tenant_name || "Azienda non indicata"}
              </span>
            </div>}
            <p className="text-xs text-[#707070] mt-3" data-testid="support-conversation-meta">
              {admin ? `${ticket.tenant_name} · ${ticket.author_email} · ` : ""}{supportDate(ticket.created_at)}
            </p>
          </div>
          <div aria-live="polite"><SupportStatus status={ticket.status} large testId="support-conversation-status" /></div>
        </div>
        {!admin && createdFor === ticketId && <div className="mt-6"><SupportResponseTimeNote created /></div>}
        <div className="py-8 space-y-6" aria-live="polite" aria-relevant="additions" data-testid="support-conversation-messages">
          {ticket.messages.map(message => (
            <SupportMessage key={message.id} message={message} admin={admin} currentEmail={user?.email}
                            customerName={ticket.author_name || ticket.author_email}
                            company={ticket.tenant_name || "Azienda non indicata"} ticketId={ticketId} />
          ))}
        </div>
        {ticket.deletion_state === "deleting" ? (
          <p className="border-t border-[#E4E3DE] pt-6 text-sm text-[#8C3A3A]" role="alert"
             data-testid="support-deletion-in-progress">Eliminazione non completata. Riprova qui sotto.</p>
        ) : (admin || ["open", "in_progress"].includes(ticket.status)) ? (
          <form onSubmit={submit} className="border-t border-[#E4E3DE] pt-7 pb-12" data-testid="support-reply-form">
            {admin && <div className="mb-6">
              <label htmlFor="support-status" className="block text-sm font-medium mb-2">Stato</label>
              <select id="support-status" value={status} onChange={event => {
                selectedStatus.current = event.target.value; setStatus(event.target.value);
              }}
                      className={`w-full sm:w-64 border rounded-sm p-3 text-sm support-ux-select-status--${status}`}
                      data-testid="support-admin-status">
                {status === "open" && <option value="open" data-testid="support-admin-status-option-open">Aperto</option>}
                <option value="in_progress" data-testid="support-admin-status-option-in-progress">In lavorazione</option>
                <option value="completed" data-testid="support-admin-status-option-completed">Completato</option>
                <option value="cancelled" data-testid="support-admin-status-option-cancelled">Annullato</option>
              </select>
            </div>}
            <label htmlFor="support-reply" className="block text-sm font-medium mb-2">
              {admin ? "Risposta (facoltativa)" : "La tua risposta"}
            </label>
            <Textarea id="support-reply" value={reply} onChange={event => setReply(event.target.value)}
                      rows={5} maxLength={10000} placeholder="La tua risposta..." className="rounded-sm bg-white resize-y"
                      data-testid="support-reply-body" />
            <div className="mt-3"><SupportFilePicker files={files} onChange={setFiles} disabled={busy}
                                                  idPrefix="support-reply" /></div>
            <Button type="submit" disabled={busy || (admin ? !reply.trim() && files.length === 0 && status === ticket.status
              : !reply.trim() && files.length === 0)}
                    className="rounded-sm bg-[#1A1A1A] text-white hover:bg-[#424242] mt-4 gap-2"
                    data-testid="support-reply-submit"><Send className="w-4 h-4" />{busy ? "Invio..." : admin ? "Salva aggiornamento" : "Invia risposta"}</Button>
          </form>
        ) : <div className="border-t border-[#E4E3DE] pt-6">
          <p className="text-sm text-[#707070]" data-testid="support-ticket-closed">
            Questo ticket è concluso. Puoi aprire un nuovo ticket per un'altra richiesta.
          </p>
          {(reply || files.length > 0) && <div className="mt-5" data-testid="support-unsent-draft">
            <label htmlFor="support-unsent-text" className="block text-xs font-medium mb-2">Testo non inviato</label>
            {reply && <Textarea id="support-unsent-text" value={reply} readOnly rows={4}
                                className="rounded-sm bg-[#F7F4EE] resize-y" data-testid="support-unsent-draft-text" />}
            {files.length > 0 && <p className="text-xs mt-2 break-words" data-testid="support-unsent-draft-files">
              Allegati non inviati: {files.map(file => file.name).join(", ")}
            </p>}
          </div>}
        </div>}
        {admin && ["completed", "cancelled"].includes(ticket.status) && (
          <div className="border-t border-[#E4E3DE] mt-8 pt-6 pb-10">
            <button type="button" ref={deleteTriggerRef} onClick={openDelete} disabled={impactLoading || deleteBusy}
                    className="inline-flex items-center gap-2 text-sm text-[#9C3730] hover:text-[#792D28] disabled:opacity-50"
                    data-testid="support-delete-open">
              <Trash2 className="w-4 h-4" />{impactLoading ? "Verifica ticket..." : "Elimina ticket"}
            </button>
          </div>
        )}
      </>}
      {confirmFirstReply && ticket && <SupportFirstReplyDialog busy={busy}
        onStay={() => setConfirmFirstReply(false)}
        onInProgress={() => sendReply("in_progress")}
        onCompleted={() => sendReply("completed")} />}
      {deleteImpact && <SupportDeleteDialog impact={deleteImpact} busy={deleteBusy} error={deleteError}
        returnFocusRef={deleteReturnFocusRef}
        onCancel={() => { setDeleteImpact(null); setDeleteError(""); }} onConfirm={confirmDelete} />}
    </div>
  );
};